import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useOrganization } from '../../context/OrganizationContext'
import { storesApi } from '../../services/storesApi'
import { CURRENCIES, getCurrencyLabel } from '../../utils/currencies'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { SkeletonFormSections } from '../../components/ui/Skeleton'
import { UploadProgressBar } from '../../components/ui/UploadProgressBar'
import { uploadProductImages } from '../../services/uploadService'
import { patchStoreMobileAppLinks } from '../../services/storeMobileAppApi'
import { appPossessive } from '../../config/brand'
import { STANDARD_ADDRESS_PLACEHOLDER } from '../../lib/formatStandardAddress'

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** Matches API `STORE_PROFILE_LIMITS` (shopora_Api `store.dto.ts`). */
const PROFILE_LIMITS = {
  aboutText: 8000,
  tagline: 255,
  sellingSummary: 5000,
  contactAddress: 512,
  businessHours: 512,
}

const textareaClass =
  'min-h-[5.5rem] w-full resize-y rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60'

function normalizeOrgCurrency(org) {
  return String(org?.currency ?? 'USD').trim().toUpperCase() || 'USD'
}

function effectiveMatchesOrgDefault(store, orgCurrency) {
  const r = String(store?.currency ?? '').trim().toUpperCase()
  return r === orgCurrency
}

export default function StoreEdit() {
  const { id: orgRouteId, storeId } = useParams()
  const navigate = useNavigate()
  const { getOrganizationById, clearError, error } = useOrganization()
  const [org, setOrg] = useState(null)
  const [store, setStore] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  const [name, setName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [sellingSummary, setSellingSummary] = useState('')
  const [aboutText, setAboutText] = useState('')
  const [tagline, setTagline] = useState('')
  const [contactAddress, setContactAddress] = useState('')
  const [businessHours, setBusinessHours] = useState('')
  const [aboutBannerUrl, setAboutBannerUrl] = useState('')
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [bannerUploadProgress, setBannerUploadProgress] = useState(0)
  const bannerInputRef = useRef(null)
  const [currencyChoice, setCurrencyChoice] = useState('')
  const [iosAppUrl, setIosAppUrl] = useState('')
  const [androidAppUrl, setAndroidAppUrl] = useState('')
  const [mobileSubmitting, setMobileSubmitting] = useState(false)
  const [mobileFormError, setMobileFormError] = useState(null)
  const [mobileSavedMsg, setMobileSavedMsg] = useState(null)

  useEffect(() => {
    clearError()
  }, [clearError, orgRouteId, storeId])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const o = await getOrganizationById(orgRouteId)
        const { items } = await storesApi.list(orgRouteId)
        const sid = String(storeId)
        const s = (Array.isArray(items) ? items : []).find((row) => String(row.id) === sid)
        if (cancelled) return
        if (!s) {
          setLoadError('Store not found')
          return
        }
        setOrg(o)
        setStore(s)
        setLoadError(null)
        const orgCur = normalizeOrgCurrency(o)
        setName(s.name ?? '')
        setSubdomain(s.subdomain ?? '')
        setContactEmail(s.contactEmail ?? '')
        setContactPhone(s.contactPhone ?? '')
        setSellingSummary(s.sellingSummary ?? '')
        setAboutText(s.aboutText ?? '')
        setTagline(s.tagline ?? '')
        setContactAddress(s.contactAddress ?? '')
        setBusinessHours(s.businessHours ?? '')
        setAboutBannerUrl(s.aboutBannerUrl ?? '')
        setCurrencyChoice(
          effectiveMatchesOrgDefault(s, orgCur) ? '' : String(s.currency || '').toUpperCase(),
        )
        setIosAppUrl(s.iosAppStoreUrl ?? '')
        setAndroidAppUrl(s.androidPlayStoreUrl ?? '')
        setMobileFormError(null)
        setMobileSavedMsg(null)
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [orgRouteId, storeId, getOrganizationById])

  async function handleSubmit(e) {
    e.preventDefault()
    setFormError(null)
    const sub = subdomain.trim().toLowerCase()
    if (!SLUG_RE.test(sub)) {
      setFormError('The web address ending must use lowercase letters, numbers, and hyphens only.')
      return
    }
    if (aboutText.length > PROFILE_LIMITS.aboutText) {
      setFormError(`About text must be at most ${PROFILE_LIMITS.aboutText} characters.`)
      return
    }
    if (tagline.length > PROFILE_LIMITS.tagline) {
      setFormError(`Tagline must be at most ${PROFILE_LIMITS.tagline} characters.`)
      return
    }
    if (sellingSummary.length > PROFILE_LIMITS.sellingSummary) {
      setFormError(`What this shop sells must be at most ${PROFILE_LIMITS.sellingSummary} characters.`)
      return
    }
    if (contactAddress.length > PROFILE_LIMITS.contactAddress) {
      setFormError(`Address must be at most ${PROFILE_LIMITS.contactAddress} characters.`)
      return
    }
    if (businessHours.length > PROFILE_LIMITS.businessHours) {
      setFormError(`Business hours must be at most ${PROFILE_LIMITS.businessHours} characters.`)
      return
    }
    clearError()
    setSubmitting(true)
    try {
      await storesApi.update(orgRouteId, storeId, {
        name: name.trim(),
        subdomain: sub,
        contactEmail: contactEmail.trim() || null,
        contactPhone: contactPhone.trim() || null,
        sellingSummary: sellingSummary.trim() || null,
        aboutText: aboutText.trim() || null,
        tagline: tagline.trim() || null,
        contactAddress: contactAddress.trim() || null,
        businessHours: businessHours.trim() || null,
        currency: currencyChoice === '' ? null : currencyChoice,
      })
      navigate(`/organizations/${orgRouteId}`, { replace: true })
    } catch (err) {
      setFormError(err?.message || 'Update failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleBannerFile(ev) {
    const file = ev.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setFormError('Please choose an image file (PNG, JPG, or WebP).')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image must be 5MB or smaller.')
      return
    }
    setUploadingBanner(true)
    setBannerUploadProgress(0)
    setFormError(null)
    try {
      const urls = await uploadProductImages([file], setBannerUploadProgress)
      const url = urls[0]
      if (!url) throw new Error('Upload did not return a URL')
      await storesApi.update(orgRouteId, storeId, { aboutBannerUrl: url })
      setAboutBannerUrl(url)
    } catch (err) {
      setFormError(err?.message || 'Banner upload failed')
    } finally {
      setUploadingBanner(false)
      setBannerUploadProgress(0)
      ev.target.value = ''
    }
  }

  async function handleMobileAppSubmit(e) {
    e.preventDefault()
    setMobileFormError(null)
    setMobileSavedMsg(null)
    setMobileSubmitting(true)
    try {
      await patchStoreMobileAppLinks(storeId, {
        iosAppStoreUrl: iosAppUrl.trim() === '' ? null : iosAppUrl.trim(),
        androidPlayStoreUrl: androidAppUrl.trim() === '' ? null : androidAppUrl.trim(),
      })
      setMobileSavedMsg('Saved.')
    } catch (err) {
      setMobileFormError(err?.message || 'Update failed')
    } finally {
      setMobileSubmitting(false)
    }
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">{loadError}</p>
        <Link to={orgRouteId ? `/organizations/${orgRouteId}` : '/organizations'}>
          <Button type="button" variant="secondary">
            Back
          </Button>
        </Link>
      </div>
    )
  }

  if (!store || !org) {
    return <SkeletonFormSections />
  }

  const orgCur = normalizeOrgCurrency(org)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Store</p>
          <h2 className="text-2xl font-semibold text-foreground">{store.name}</h2>
          <p className="text-muted-foreground">
            Organisation: {org.name} · Company default currency: {orgCur}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/organizations/${orgRouteId}`}>
            <Button type="button" variant="secondary">
              Back to organisation
            </Button>
          </Link>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}
      {formError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {formError}
        </div>
      ) : null}

      <Card>
        <CardHeader
          title="Store details"
          description="The short web address identifies this shop’s link. Set storefront currency below — that is what customers pay in, not the organisation default alone."
        />
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            id="store-name"
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
          />
          <Input
            id="store-subdomain"
            label="Web address ending"
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value)}
            required
            placeholder="my-store"
          />
          <p className="-mt-2 text-xs text-muted-foreground">
            The part of the link after your domain — lowercase, hyphens allowed (e.g. my-boutique). Must be unique
            across all shops.
          </p>
          <Input
            id="store-email"
            type="email"
            label="Contact email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
          <Input
            id="store-phone"
            label="Contact phone"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          />
          <div>
            <label htmlFor="store-selling" className="mb-1.5 block text-sm font-medium text-foreground">
              What this shop sells
            </label>
            <textarea
              id="store-selling"
              rows={3}
              disabled={submitting}
              value={sellingSummary}
              onChange={(e) => setSellingSummary(e.target.value)}
              maxLength={PROFILE_LIMITS.sellingSummary}
              className={textareaClass}
              placeholder="e.g. Ready-to-wear dresses and custom sewing for women"
            />
            <p className="mt-1 text-xs tabular-nums text-muted-foreground">
              {sellingSummary.length}/{PROFILE_LIMITS.sellingSummary}
            </p>
          </div>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-foreground">About page banner</span>
            <p className="mb-2 text-xs text-muted-foreground">
              Wide image for the customer About page (not the header logo).
            </p>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleBannerFile}
            />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <Button
                type="button"
                variant="secondary"
                disabled={uploadingBanner || submitting}
                onClick={() => bannerInputRef.current?.click()}
              >
                {uploadingBanner ? 'Uploading…' : 'Upload banner'}
              </Button>
              {aboutBannerUrl ? (
                <img
                  src={aboutBannerUrl}
                  alt=""
                  className="w-full max-w-xl rounded-lg border border-border object-cover aspect-[21/9] max-h-48"
                />
              ) : (
                <span className="text-xs text-muted-foreground">No banner</span>
              )}
            </div>
            {uploadingBanner ? (
              <UploadProgressBar active={uploadingBanner} label="Uploading…" progress={bannerUploadProgress} />
            ) : null}
          </div>
          <Input
            id="store-tagline"
            label="Tagline (online shop)"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            maxLength={PROFILE_LIMITS.tagline}
          />
          <div>
            <label htmlFor="store-about" className="mb-1.5 block text-sm font-medium text-foreground">
              About (online shop)
            </label>
            <textarea
              id="store-about"
              rows={6}
              disabled={submitting}
              value={aboutText}
              onChange={(e) => setAboutText(e.target.value)}
              maxLength={PROFILE_LIMITS.aboutText}
              className={textareaClass}
              placeholder="Longer text for the customer-facing About page"
            />
            <p className="mt-1 text-xs tabular-nums text-muted-foreground">
              {aboutText.length}/{PROFILE_LIMITS.aboutText}
            </p>
          </div>
          <div>
            <label htmlFor="store-address" className="mb-1.5 block text-sm font-medium text-foreground">
              Address (online shop)
            </label>
            <textarea
              id="store-address"
              rows={3}
              disabled={submitting}
              value={contactAddress}
              onChange={(e) => setContactAddress(e.target.value)}
              maxLength={PROFILE_LIMITS.contactAddress}
              className={textareaClass}
              placeholder={STANDARD_ADDRESS_PLACEHOLDER}
            />
            <p className="mt-1 text-xs tabular-nums text-muted-foreground">
              {contactAddress.length}/{PROFILE_LIMITS.contactAddress}
            </p>
          </div>
          <Input
            id="store-hours"
            label="Business hours (online shop)"
            value={businessHours}
            onChange={(e) => setBusinessHours(e.target.value)}
            maxLength={PROFILE_LIMITS.businessHours}
          />
          <div className="space-y-3 rounded-2xl border border-border bg-muted/30 p-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Storefront currency</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                This is what customers see on the online shop (prices, cart, checkout). The company
                default is only a fallback when this shop does not set its own override.
              </p>
            </div>
            <Select
              id="store-currency"
              label="Currency for this shop"
              value={currencyChoice}
              onChange={(e) => setCurrencyChoice(e.target.value)}
            >
              <option value="">
                Use company default ({getCurrencyLabel(orgCur)}) — recommended
              </option>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                  {c.code === orgCur ? ' (same as company default)' : ''}
                </option>
              ))}
            </Select>
            {currencyChoice === '' ? (
              <p className="text-xs text-muted-foreground">
                Storefront will use <span className="font-medium text-foreground">{getCurrencyLabel(orgCur)}</span>.
                Changing the organisation currency later will update this shop if it stays on the
                company default.
              </p>
            ) : currencyChoice !== orgCur ? (
              <div
                className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-950 dark:text-amber-100"
                role="status"
              >
                <p className="font-semibold">Store override active</p>
                <p className="mt-1">
                  Customers will see <span className="font-medium">{getCurrencyLabel(currencyChoice)}</span>,
                  not the company default ({getCurrencyLabel(orgCur)}). Changing the organisation
                  currency will <span className="font-medium">not</span> update this shop while this
                  override is set. Choose “Use company default” above if this shop should price in{' '}
                  {getCurrencyLabel(orgCur)}.
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Same code as the company default — saving will clear any sticky override so this shop
                follows the organisation going forward.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save changes'}
            </Button>
            <Link to={`/organizations/${orgRouteId}`}>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Customer mobile app"
          description={`Leave blank to use ${appPossessive()} general app links for iPhone or Android. If this shop has its own app in the stores, paste the App Store or Google Play link here.`}
        />
        {mobileFormError ? (
          <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {mobileFormError}
          </div>
        ) : null}
        {mobileSavedMsg ? (
          <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200">
            {mobileSavedMsg}
          </div>
        ) : null}
        <form onSubmit={handleMobileAppSubmit} className="space-y-6">
          <Input
            id="store-ios-app"
            type="url"
            label="App Store (optional)"
            value={iosAppUrl}
            onChange={(e) => setIosAppUrl(e.target.value)}
            placeholder="https://apps.apple.com/…"
            autoComplete="off"
          />
          <Input
            id="store-android-app"
            type="url"
            label="Google Play (optional)"
            value={androidAppUrl}
            onChange={(e) => setAndroidAppUrl(e.target.value)}
            placeholder="https://play.google.com/…"
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">
            Use secure (https) links that open the app in Apple’s or Google’s store. Clear both fields to go back to
            {appPossessive()} default app links for customers.
          </p>
          <Button type="submit" disabled={mobileSubmitting}>
            {mobileSubmitting ? 'Saving…' : 'Save app links'}
          </Button>
        </form>
      </Card>
    </div>
  )
}
